import {
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PhoneIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
// Removed unused Field/Label/Input/Select imports after migrating to shared form
import { Heading } from '@/components/ui/catalyst/heading';
import { Text } from '@/components/ui/catalyst/text';
import { useMatter } from '@/hooks/useMatter';
import { useStartEnhancedBlandCallWorkflowMutation } from '@/services/api/blandService';
import {
  TreatmentRecordRequestChannelType,
  TreatmentRecordRequestSchema,
  VoiceAiCallArgs,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { useGetMedicalProviderQuery } from '@/services/api/treatmentService';
import {
  getIntervalLabel,
  getNextCallStatus,
  isVoiceAIApplicable,
} from '@/utils/voiceAiScheduling';

import { VoiceAIEditableField } from '.';
import {
  useSaveVoiceAICallFirm,
  useVoiceAICallFormData,
} from './hooks/useVoiceAICall';
import { useVoiceAIStatus } from './hooks/useVoiceAIStatus';
import { VoiceAICallForm } from './VoiceAICallForm';

interface VoiceAICallTabProps {
  requestId: string;
  providerId: string;
}

export const VoiceAICallTab: React.FC<VoiceAICallTabProps> = ({
  requestId,
  providerId,
}) => {
  const { matterId } = useMatter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingToBpo, setIsSendingToBpo] = useState(false);
  const lastCallClickRef = useRef(0);
  const lastBpoClickRef = useRef(0);

  // Fetch data for prefilling
  const { data: matterDetails } = useGetMatterDetailsQuery(matterId);
  const { data: provider } = useGetMedicalProviderQuery({
    matterId,
    providerId,
  });
  const { data: request, refetch: refetchRequest } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
      {
        matterId,
        providerId,
        requestId,
      },
    );

  // Form state (controlled) and save hook
  const { values, handleChange, isLoading } = useVoiceAICallFormData({
    matterId,
    providerId,
    requestId,
  });
  const { saveVoiceAicallFirm } = useSaveVoiceAICallFirm({
    matterId,
    providerId,
    requestId,
  });

  // Mutation hooks
  const [sendBlandCall] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsSendBlandCallCreateMutation();
  const [startEnhancedWorkflow] = useStartEnhancedBlandCallWorkflowMutation();
  const handleInitiateCall = async () => {
    if (!request || !matterDetails) {
      toast.error('Missing request or matter details');
      return;
    }

    // 500ms debounce to prevent accidental double-clicks
    const now = Date.now();
    if (now - lastCallClickRef.current < 500) {
      toast.info('Please wait before retrying');
      return;
    }
    lastCallClickRef.current = now;

    type ClientLite = { date_of_birth?: string; is_primary?: boolean };
    const clients = matterDetails.clients as ClientLite[] | undefined;
    const primaryClient = clients?.find((c) => c.is_primary) || clients?.[0];

    const method = (request.channel?.type ||
      'portal') as TreatmentRecordRequestChannelType;
    let request_method_target = '';
    if (method === 'fax') request_method_target = request.channel?.fax || '';
    else if (method === 'email')
      request_method_target = request.channel?.email || '';
    else if (method === 'mail')
      request_method_target = request.channel?.address || '';
    const contact_number = request.channel?.phone_number || '';

    const args: VoiceAiCallArgs = {
      patient_information: {
        client_name: matterDetails.name || '',
        client_date_of_birth: primaryClient?.date_of_birth || '',
      },
      request_details: {
        request_method: method,
        request_sent_at: new Date().toISOString(),
        request_method_target,
        contact_number,
      },
      requestee_information: {
        requestee_name:
          matterDetails.firm.full_name || matterDetails.firm.name || '',
      },
    };

    setIsSubmitting(true);
    try {
      // Implicitly save form details before initiating call
      await saveVoiceAicallFirm(values);

      await sendBlandCall({
        matterId,
        providerId,
        requestId,
        voiceAiCallArgs: args,
      }).unwrap();
      toast.success('Voice AI call initiated successfully');
      refetchRequest();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to send Voice AI call:', error);
      toast.error('Failed to initiate Voice AI call');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendToBpo = async () => {
    if (!request?.task_id) {
      toast.error('No task associated with this request');
      return;
    }

    // 500ms debounce to prevent accidental double-clicks
    const now = Date.now();
    if (now - lastBpoClickRef.current < 500) {
      toast.info('Please wait before retrying');
      return;
    }
    lastBpoClickRef.current = now;

    setIsSendingToBpo(true);
    try {
      // Start enhanced workflow with maxCallsOverride=0 to skip directly to BPO
      const result = await startEnhancedWorkflow({
        matterId,
        taskId: request.task_id,
        preferredPhoneNumber: request.channel?.phone_number || undefined,
        maxCallsOverride: 0, // Skip AI calls, go directly to BPO
      }).unwrap();

      if (result.terminated_existing) {
        toast.success(
          'Existing workflow terminated and new workflow started',
        );
      } else {
        toast.success('Request sent to BPO team');
      }
      refetchRequest();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to send to BPO:', error);
      toast.error('Failed to send to BPO');
    } finally {
      setIsSendingToBpo(false);
    }
  };

  // Use Voice AI status hook - must be called before any conditional returns
  const voiceAI = useVoiceAIStatus(
    request || ({} as unknown as TreatmentRecordRequestSchema),
  );
  const { message: nextCallMessage } = getNextCallStatus(
    voiceAI.nextCallDate,
    request?.channel?.type,
    request?.status,
  );

  if (!request || !provider || !matterDetails) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <Heading level={2}>Voice AI Call</Heading>
        <Text className="mt-2 text-zinc-500">
          Configure and initiate an automated voice call for this records
          request
        </Text>
      </div>

      {/* Portal request warning */}
      {request?.channel?.type === 'portal' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <Text className="text-sm font-medium text-amber-900">
                Portal Request
              </Text>
              <Text className="text-sm text-amber-700 mt-1">
                Voice AI follow-up calls are not applicable for portal requests
                as they are tracked automatically through the provider&apos;s
                portal.
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Voice AI Scheduling Information */}
      <div
        className={`rounded-lg border p-6 ${(() => {
          if (request?.channel?.type === 'portal') {
            return 'border-gray-200 bg-gray-50 opacity-60';
          }
          if (isVoiceAIApplicable(request?.channel?.type, request?.status)) {
            return 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50';
          }
          return 'border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50';
        })()}`}
      >
        <div className="flex items-center justify-between mb-4">
          <Heading level={3} className="flex items-center gap-2">
            <PhoneIcon className="h-5 w-5 text-indigo-600" />
            Voice AI Scheduling
          </Heading>
          {request && (
            <VoiceAIEditableField
              request={request}
              matterId={matterId}
              providerId={providerId}
              requestId={requestId}
              onUpdate={refetchRequest}
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-gray-500" />
            <div>
              <Text className="text-xs text-gray-500">Current Interval</Text>
              <Text className="text-sm font-medium">
                {getIntervalLabel(voiceAI.interval)}
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-gray-500" />
            <div>
              <Text className="text-xs text-gray-500">Last Called</Text>
              <Text className="text-sm font-medium">
                {voiceAI.lastCalledAt
                  ? format(new Date(voiceAI.lastCalledAt), 'MMM d, yyyy h:mm a')
                  : 'Never'}
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-gray-500" />
            <div>
              <Text className="text-xs text-gray-500">
                {request?.status === 'pending'
                  ? 'Follow-ups Begin'
                  : 'Next Scheduled Call'}
              </Text>
              <Text className="text-sm font-medium">
                {request?.status === 'pending'
                  ? 'After submission'
                  : nextCallMessage}
              </Text>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-white/50 rounded-md">
          <div className="flex gap-2">
            <InformationCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <Text className="text-sm text-gray-600">
              <span className="font-medium">{voiceAI.statusMessage}</span>
              {voiceAI.canEdit && request?.status !== 'pending' && (
                <span>
                  {' '}
                  • You can customize the interval using the edit button above.
                </span>
              )}
            </Text>
          </div>
        </div>
      </div>

      {/* Edit details using shared form */}
      {!isLoading && (
        <VoiceAICallForm values={values} onChange={handleChange} />
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        {request?.task_id && (
          <Button
            outline
            onClick={handleSendToBpo}
            disabled={isSendingToBpo || isSubmitting}
          >
            <UserGroupIcon className="h-4 w-4 mr-2" />
            {isSendingToBpo ? 'Sending to BPO...' : 'Send to BPO'}
          </Button>
        )}
        <Button onClick={handleInitiateCall} disabled={isSubmitting}>
          {isSubmitting ? 'Initiating Call...' : 'Initiate Voice AI Call'}
        </Button>
      </div>
    </div>
  );
};
